/**
 * 조준선 기하 — AimLineView(updateAimLine)의 순수 계산 분리 (Phaser 비의존, 단위테스트 대상).
 * 각도/페이드 진행도는 이미 systems/aimAngle·aimLineFade가 담당하고, 여기서는 가시 길이 + 선분 좌표 +
 * 페이드 alpha만 다룬다. 실제 그리기(Graphics.lineBetween)는 AimLineView가 이 결과를 사용한다.
 */
import { aimBaseAngleRad } from "../systems/aimAngle.js";
import { aimFadeProgress, aimTargetChanged } from "../systems/aimLineFade.js";

export interface AimPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * 조준선 가시 길이 = maxLen × progress.
 * 타겟은 방향만 정하고, 선 길이는 고정 가이드 길이를 유지한다.
 */
export function aimVisibleLength(
  target: { x: number; y: number } | null,
  originX: number,
  originY: number,
  maxLen: number,
  startOffset: number,
  progress: number,
): number {
  void target;
  void originX;
  void originY;
  void startOffset;
  return maxLen * progress;
}

export interface AimSegment {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
  /** 선분 시작점의 정규화 위치 (0 = origin쪽, 1 = 끝). 페이드 alpha 계산용. */
  readonly t0: number;
}

/** start→end를 count등분한 선분 좌표 배열 (각 선분 시작점의 t0 포함). */
export function aimSegments(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  count: number,
): AimSegment[] {
  const segs: AimSegment[] = [];
  for (let i = 0; i < count; i++) {
    const t0 = i / count;
    const t1 = (i + 1) / count;
    segs.push({
      x0: startX + (endX - startX) * t0,
      y0: startY + (endY - startY) * t0,
      x1: startX + (endX - startX) * t1,
      y1: startY + (endY - startY) * t1,
      t0,
    });
  }
  return segs;
}

/** 코어/글로우 라인의 origin→끝 이차 페이드 alpha (t0=0 최대, t0→1 에서 0). */
export function aimSegmentAlpha(baseAlpha: number, t0: number): number {
  return baseAlpha * (1 - t0) * (1 - t0);
}

export interface AimLineFrameInput {
  readonly nowMs: number;
  readonly origin: AimPoint;
  readonly target: AimPoint | null;
  readonly prevTarget: AimPoint | null;
  readonly fadeStartMs: number;
  readonly maxLen: number;
  readonly startOffset: number;
  readonly fadeMs: number;
  readonly segmentCount: number;
}

export interface AimLineFrame {
  readonly prevTarget: AimPoint | null;
  readonly fadeStartMs: number;
  readonly segments: readonly AimSegment[];
}

/** AimLineView가 보유한 fade 상태와 현재 target을 받아 이번 프레임의 그리기 선분을 산출한다. */
export function buildAimLineFrame(input: AimLineFrameInput): AimLineFrame {
  const targetChanged = aimTargetChanged(input.prevTarget, input.target);
  const fadeStartMs = targetChanged ? input.nowMs : input.fadeStartMs;
  const prevTarget = targetChanged
    ? input.target
      ? { x: input.target.x, y: input.target.y }
      : null
    : input.prevTarget;

  const progress = aimFadeProgress(input.nowMs - fadeStartMs, input.fadeMs);
  const angle = aimBaseAngleRad(input.origin, input.target);
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);
  const len = aimVisibleLength(
    input.target,
    input.origin.x,
    input.origin.y,
    input.maxLen,
    input.startOffset,
    progress,
  );
  if (len <= 0) return { prevTarget, fadeStartMs, segments: [] };

  const startX = input.origin.x + dx * input.startOffset;
  const startY = input.origin.y + dy * input.startOffset;
  const endX = input.origin.x + dx * (input.startOffset + len);
  const endY = input.origin.y + dy * (input.startOffset + len);
  return {
    prevTarget,
    fadeStartMs,
    segments: aimSegments(startX, startY, endX, endY, input.segmentCount),
  };
}
